import {
  ActionItem,
  Button,
  DotsHorizontalIcon,
  Edit03Icon,
  FolderPlusIcon,
  Menu,
  MenuItem,
  MenuPopover,
  MenuTrigger,
  Trash01Icon,
  XCircleIcon,
} from '@translated/babele-react'

import {useFetcher, useLoaderData} from '@remix-run/react'
import {createContext, useContext, useEffect, useRef, useState} from 'react'
import {type Upload} from 'upload'
import {z} from 'zod'

import {getTxmUploader} from '~/models/memory/memory.client'
import {type Memory} from '~/models/memory/memory.server'
import {Progress, ProgressIndicator} from '~/ui/Progress'
import {AddMemoryDialog} from './AddMemoryDialog'
import {DeleteMemoryDialog} from './DeleteMemoryDialog'
import {type loader} from './route'
import {type loader as importJobLoader} from '~/routes/_loader.getImportJob.$tmxId'
import {ToggleMemory} from './ToggleMemory'
import {UpdateMemoryDialog} from './UpdateMemoryDialog'

const JOB_CHECK_INTERVAL_MS = 1000
const JOB_COMPLETED_STATE_MS = 5000

const uploadResponseSchema = z
  .string()
  .transform((val, ctx) => {
    try {
      return JSON.parse(val)
    } catch (e) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Invalid JSON',
        path: ['data'],
      })
    }
  })
  .pipe(z.object({data: z.object({id: z.string()})}))
  .transform(({data}) => data)

type TmxUpload = {
  state: 'uploading' | 'importing' | 'done' | 'error'
  uploadProgress: number
  fileName?: string
  jobId?: string
}

const MemoryRow = ({memory}: {memory: Memory & {isActive: boolean}}) => {
  const inputFileRef = useRef<HTMLInputElement>(null)
  const uploaderRef = useRef<Upload | null>(null)

  const [tmxUpload, setTmxUpload] = useState<TmxUpload | null>(null)

  const jobFetcher = useFetcher<typeof importJobLoader>({
    key: tmxUpload?.jobId ?? 'none',
  })
  const load = jobFetcher.load

  const loaderData = useLoaderData<typeof loader>()

  const importJobProgress = jobFetcher.data?.data.progress ?? 0

  const activeMemoriesContext = useActiveMemories()

  useEffect(() => {
    let intervalId: NodeJS.Timeout

    if (tmxUpload?.state == 'importing') {
      intervalId = setInterval(() => {
        load(`/getImportJob/${tmxUpload.jobId}`)
      }, JOB_CHECK_INTERVAL_MS)
    }
    return () => {
      clearInterval(intervalId)
    }
  }, [tmxUpload, load])

  useEffect(() => {
    let timeoutId: NodeJS.Timeout

    if (importJobProgress == 1) {
      setTmxUpload((prev) => ({...prev, uploadProgress: 1, state: 'done'}))

      timeoutId = setTimeout(() => {
        setTmxUpload(null)
        activeMemoriesContext?.onActiveMemoriesChange()
      }, JOB_COMPLETED_STATE_MS)
    }
    return () => {
      clearTimeout(timeoutId)
    }
  }, [importJobProgress, activeMemoriesContext])

  if (tmxUpload?.state == 'done') {
    return (
      <div className="rounded-lg bg-success-100 p-6 flex justify-between items-center gap-4">
        <div className="text-nowrap">Uploaded Completed</div>
      </div>
    )
  }

  if (tmxUpload?.state == 'error') {
    return (
      <div className="rounded-lg bg-critical-100 p-6 flex justify-between items-center gap-4">
        <div className="text-nowrap">Error while uploading the tmx</div>
      </div>
    )
  }

  if (tmxUpload?.state == 'uploading' || tmxUpload?.state == 'importing')
    return (
      <div className="rounded-lg bg-notice-100 p-6 flex justify-between items-center gap-4">
        <div className="text-nowrap">
          {tmxUpload.state == 'uploading' && tmxUpload.uploadProgress < 1
            ? `Adding ${tmxUpload.fileName} to ${memory.name}`
            : tmxUpload.state != 'importing'
              ? 'Preparing the file for processing...'
              : 'Processing the file. Please do not close the page'}
        </div>
        <Progress
          aria-label="TMX uploading"
          value={tmxUpload.uploadProgress * 50 + importJobProgress * 50}
          className="bg-notice-400 max-w-xs ml-auto"
          animate={false}
        >
          <ProgressIndicator className="bg-notice-800" />
        </Progress>
        <Button
          variant={{size: 'sm', mode: 'ghost'}}
          aria-label="Cancel the upload"
          onClick={() => {
            uploaderRef.current?.abort()
            setTmxUpload(null)
          }}
        >
          <XCircleIcon />
        </Button>
      </div>
    )

  return (
    <div className="rounded-lg bg-neutral-50 p-6 flex justify-between items-center">
      <div className="flex items-center gap-3">
        <ToggleMemory memory={memory} />
        {memory.name}
      </div>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          aria-label="Delete memory"
          variant={{size: 'sm', mode: 'ghost'}}
          onClick={() => {
            inputFileRef.current?.click()
          }}
        >
          <FolderPlusIcon />
        </Button>
        <input
          type="file"
          ref={inputFileRef}
          aria-label="Upload TMX file"
          className="sr-only"
          tabIndex={-1}
          name="tmx"
          accept=".tmx"
          onChange={async (e) => {
            if (e.target.files && e.target.files[0] && loaderData.licenseKey) {
              try {
                setTmxUpload({
                  state: 'uploading',
                  uploadProgress: 0,
                  fileName: e.target.files[0].name,
                })
                const uploader = getTxmUploader({
                  id: memory.id,
                  file: e.target.files[0],
                  licenseKey: loaderData.licenseKey,
                  onProgressChange: (value) =>
                    setTmxUpload((prev) => {
                      return {
                        ...prev,
                        state: 'uploading',
                        uploadProgress: value,
                      }
                    }),
                })
                uploaderRef.current = uploader
                const res = await uploader.upload()
                if (res.status == 200) {
                  const {id} = uploadResponseSchema.parse(res.data)
                  setTmxUpload((prev) => ({
                    ...prev,
                    state: 'importing',
                    uploadProgress: 1,
                    jobId: id,
                  }))
                  return
                }
                throw new Error('Failed to upload the file')
              } catch (e) {
                setTmxUpload({state: 'error', uploadProgress: 0})
              }
            }
          }}
        />
        <Menu variant={{size: 'sm'}} placement="bottom-end">
          <MenuTrigger
            aria-label="Memory Menu"
            variant={{size: 'sm', mode: 'ghost'}}
          >
            <DotsHorizontalIcon />
          </MenuTrigger>
          <MenuPopover>
            <UpdateMemoryDialog memory={memory}>
              <MenuItem
                className="flex items-center gap-2"
                render={<ActionItem variant={{size: 'sm'}} />}
              >
                <Edit03Icon />
                Rename
              </MenuItem>
            </UpdateMemoryDialog>
            <DeleteMemoryDialog memory={memory}>
              <MenuItem
                className="flex items-center gap-2"
                render={<ActionItem variant={{size: 'sm'}} />}
              >
                <Trash01Icon />
                Delete
              </MenuItem>
            </DeleteMemoryDialog>
          </MenuPopover>
        </Menu>
      </div>
    </div>
  )
}

const ActiveMemories = createContext<{
  onActiveMemoriesChange: () => void
} | null>(null)

export const useActiveMemories = () => {
  return useContext(ActiveMemories)
}

export function MemoryHandler({
  memories,
  onActiveMemoriesChange,
}: {
  memories?: Array<Memory & {isActive: boolean}>
  onActiveMemoriesChange: () => void
}) {
  return (
    <ActiveMemories.Provider value={{onActiveMemoriesChange}}>
      <div className="border-1 border-neutral-400 rounded-lg p-8 bg-neutral-25 flex flex-col gap-6">
        <div className="flex items-center justify-between">
          <div className="text-display-sm">Translation memories</div>
          <AddMemoryDialog>
            <Button
              className="ml-auto"
              variant={{tone: 'primary', mode: 'outline'}}
            >
              New memory
            </Button>
          </AddMemoryDialog>
        </div>
        {memories?.length ? (
          <div className="flex flex-col gap-2">
            {memories
              ?.sort((a, b) => {
                return new Date(a.creationDate) > new Date(b.creationDate)
                  ? -1
                  : 1
              })
              .map((memory) => <MemoryRow key={memory.id} memory={memory} />)}
          </div>
        ) : null}
      </div>
    </ActiveMemories.Provider>
  )
}
